import { TextField, useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { IconButton } from '@calendly/ui/components/icon-button'
import { DeleteIcon } from '@calendly/ui/components/icons'
import { BodyText } from '@calendly/ui/components/typography'
import { useEffect, useRef } from 'react'

import { useAdvancedSortable } from 'components/core/sortable'
import { UrlField } from 'components/event_type_edit'

import { useErrorAfterInitialTouch } from '../../hooks'
import { StatusField } from '../status_field'
import { HeaderWithIcon, InlineEditingContainer, InputContainer, StyledDragIcon, SubsectionItemHeader } from '../styles'

export const CustomLink = ({ index, link }) => {
  const ref = useRef()
  const { values: { customLinks }, setFieldValue, setFieldTouched } = useFormContext()

  const contentFieldName = `customLinks.${index}.content`
  const urlFieldName = `customLinks.${index}.url`

  useErrorAfterInitialTouch(contentFieldName)
  useErrorAfterInitialTouch(urlFieldName)

  const {
    sortable: {
      isDragging,
      attributes,
      listeners,
      setNodeRef
    },
    itemStyle,
  } = useAdvancedSortable({ id: link.id })

  useEffect(() => {
    if (!ref.current) { return }

    if (`${link.id}`.startsWith('new')) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      ref.current.focus()
    }
  }, [link.id])

  const deleteLink = () => {
    let tempLinks = [...customLinks]
    const removedLink = tempLinks.splice(index, 1)[0]
    tempLinks = tempLinks.map((tempLink, newIndex) => ({
      ...tempLink,
      position: newIndex
    }))

    const isNewLink = String(removedLink.id).startsWith('new')
    if (isNewLink) {
      setFieldValue('customLinks', tempLinks)
    } else {
      setFieldValue('customLinks', [...tempLinks, {
        ...removedLink,
        enabled: false,
        _destroy: true
      }])
    }
    setFieldTouched('customLinks', true)
  }

  if (link._destroy) return null

  return (
    <InlineEditingContainer
      ref={setNodeRef}
      isDragging={isDragging}
      {...itemStyle}
      id={`custom-link-${link.position}`}
      data-testid={`custom-link-${index}`}
    >
      <SubsectionItemHeader>
        <HeaderWithIcon>
          <StyledDragIcon {...attributes} {...listeners} className="drag-handler" />
          <BodyText size="medium" weight="bold">
            {I18n.t('publisher.event_type_editor.sections.confirmation_page.custom_link.header',
              { position: link.position + 1 })}
          </BodyText>
        </HeaderWithIcon>
        <IconButton
          aria-label={I18n.t('publisher.action.delete')}
          onClick={deleteLink}
          size="small"
          title={I18n.t('publisher.action.delete')}
        >
          <DeleteIcon />
        </IconButton>
      </SubsectionItemHeader>
      <InputContainer>
        <BodyText size="medium">
          {I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.custom_link.link_name')}
        </BodyText>
        <TextField
          autoFocus
          aria-label={I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.custom_link.link_name')}
          name={contentFieldName}
        />
      </InputContainer>
      <InputContainer>
        <BodyText size="medium">
          {I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.custom_link.link_url')}
        </BodyText>
        <UrlField
          name={urlFieldName}
          aria-label={I18n.t('publisher.event_type_editor.sections.booking_page_options.confirmation_page.custom_link.link_url')}
        />
      </InputContainer>
      <StatusField name={`customLinks.${index}.enabled`} />
    </InlineEditingContainer>
  )
}
